import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { siteConfig } from '../data/mock';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        <div className="bg-bg-primary py-20">
          <div className="px-6 lg:px-12 xl:px-20 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-medium text-text-primary mb-8">
              Privacy Policy
            </h1>
            <p className="text-sm text-text-light mb-8">
              Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="prose prose-lg max-w-none text-text-secondary space-y-8">
              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">1. Introduction</h2>
                <p>
                  HighReach Apps ("we", "us", or "our") is committed to protecting your privacy. This Privacy 
                  Policy explains how we collect, use, disclose, and safeguard your information when you use 
                  our website building and client portal services ("Services").
                </p>
                <p>
                  By using our Services, you agree to the collection and use of information in accordance with 
                  this Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">2.1 Information You Provide</h3>
                <p>We collect information that you provide directly to us, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Business name and contact information</li>
                  <li>Email address and phone number</li>
                  <li>Branding preferences and design requirements</li>
                  <li>Domain information</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                  <li>Any other information you choose to provide</li>
                </ul>

                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">2.2 Information Collected Automatically</h3>
                <p>When you use our Services, we may automatically collect certain information, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, time spent, features used)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>

                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">2.3 Client Data</h3>
                <p>
                  When you use our client portal services, we may collect and process information about your 
                  clients, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Client names and contact information</li>
                  <li>Appointment bookings and history</li>
                  <li>Payment information (processed securely)</li>
                  <li>Any other information your clients provide through the portal</li>
                </ul>
                <p>
                  This data is collected on your behalf and is subject to your own privacy practices. You are 
                  responsible for ensuring compliance with applicable data protection laws regarding your clients' data.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">3. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Build and customise your website and client portal</li>
                  <li>Provide, maintain, and improve our Services</li>
                  <li>Process payments and manage your account</li>
                  <li>Communicate with you about your account and our Services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Send you updates, newsletters, and marketing communications (you may opt out at any time)</li>
                  <li>Monitor and analyse usage patterns and trends</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">4. How We Share Your Information</h2>
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
                
                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">4.1 Service Providers</h3>
                <p>
                  We may share your information with third-party service providers who perform services on our 
                  behalf, such as payment processing, hosting, analytics, and customer support.
                </p>

                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">4.2 Legal Requirements</h3>
                <p>
                  We may disclose your information if required to do so by law or in response to valid requests 
                  by public authorities.
                </p>

                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                  as part of that transaction.
                </p>

                <h3 className="text-xl font-medium text-text-primary mt-6 mb-3">4.4 With Your Consent</h3>
                <p>
                  We may share your information with your consent or at your direction.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organisational measures to protect your personal information 
                  against unauthorised access, alteration, disclosure, or destruction. However, no method of 
                  transmission over the Internet or electronic storage is 100% secure.
                </p>
                <p>
                  We use industry-standard encryption for data transmission and secure payment processing. Your 
                  payment information is processed by trusted third-party payment processors and is not stored on 
                  our servers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">6. Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar tracking technologies to track activity on our Services and store 
                  certain information. Cookies are files with a small amount of data that may include an anonymous 
                  unique identifier.
                </p>
                <p>
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. 
                  However, if you do not accept cookies, you may not be able to use some portions of our Services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">7. Your Rights</h2>
                <p>Under applicable data protection laws, including GDPR, you have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request access to your personal information</li>
                  <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Erasure:</strong> Request deletion of your personal information</li>
                  <li><strong>Restriction:</strong> Request restriction of processing of your personal information</li>
                  <li><strong>Portability:</strong> Request transfer of your personal information to another service</li>
                  <li><strong>Objection:</strong> Object to processing of your personal information</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
                </ul>
                <p>
                  To exercise these rights, please contact us using the contact information provided below.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">8. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to provide our Services and comply 
                  with legal obligations. If you cancel your account, we will retain your information for a 
                  reasonable period to comply with legal requirements and resolve disputes.
                </p>
                <p>
                  Preview websites and portals that are not activated will be removed after 14 days, along with 
                  associated data.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">9. Children's Privacy</h2>
                <p>
                  Our Services are not intended for individuals under the age of 18. We do not knowingly collect 
                  personal information from children. If you become aware that a child has provided us with 
                  personal information, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">10. International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your country of 
                  residence. These countries may have data protection laws that differ from those in your country.
                </p>
                <p>
                  We ensure that appropriate safeguards are in place to protect your information in accordance 
                  with this Privacy Policy and applicable data protection laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by 
                  posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
                <p>
                  You are advised to review this Privacy Policy periodically for any changes. Changes to this 
                  Privacy Policy are effective when they are posted on this page.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">12. Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy or wish to exercise your rights, please 
                  contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li>Email: {siteConfig.email}</li>
                  <li>Phone: {siteConfig.phone}</li>
                </ul>
                <p className="mt-4">
                  For GDPR-related inquiries, you also have the right to lodge a complaint with your local 
                  data protection authority.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
